import { Injectable } from '@angular/core';
import { INavData } from '@coreui/angular-pro';
import { CredoMenuService } from 'credo';

// mnlo exposes a deliberately small menu: Dashboard, Informationen, Meine Karten,
// Buchungen, Termine, Community. We extend the shared CredoMenuService and fully override
// createNavItems instead of filtering, so the menu is explicit and the shared lib
// stays untouched. Translation keys are reused from the fac3less scope.
@Injectable({
  providedIn: 'root',
})
export class MnloMenuService extends CredoMenuService {
  protected override createNavItems(lang: string, t: (key: string) => string): INavData[] {
    return [
      {
        name: t('dashboard'),
        url: `/${lang}/dashboard`,
        iconComponent: { name: 'cil-speedometer' },
      },
      {
        name: t('menu.info'),
        url: `/${lang}/media/info`,
        iconComponent: { name: 'info' },
      },
      {
        name: t('mycards.menu'),
        url: `/${lang}/mycards`,
        iconComponent: { name: 'cil-credit-card' },
      },
      {
        name: t('bookings_menu'),
        url: `/${lang}/bookings`,
        iconComponent: { name: 'cil-notes' },
      },
      {
        name: t('appointments.menu'),
        url: `/${lang}/appointments`,
        iconComponent: { name: 'cil-calendar' },
      },
      {
        name: t('menu.Community'),
        url: `/${lang}/community`,
        iconComponent: { name: 'cil-chat-bubble' },
        attributes: { 'data-visible': 'flag:community_enabled' },
      },

      // --- Admin area (only shown to admins; super-admin items to user id 1) ---
      ...this.adminNavItems(lang, t),
    ];
  }

  // Admin menu items, mirroring the credo/fac3less admin section. Each is gated
  // by data-visible so the base MenuService filters them out for non-admins.
  private adminNavItems(lang: string, t: (key: string) => string): INavData[] {
    const admin = (text: string) => ({ color: 'danger', text });
    const isAdmin = { 'data-visible': 'isAdmin' };
    return [
      {
        name: t('admin_dashboard'),
        url: `/${lang}/admin/dashboard`,
        iconComponent: { name: 'cil-shield-alt' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        name: t('users'),
        url: `/${lang}/admin/users`,
        iconComponent: { name: 'cil-people' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        name: t('viacarte_admin.menu'),
        url: `/${lang}/admin/viacarte`,
        iconComponent: { name: 'cil-credit-card' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        name: t('card_partner_admin.menu'),
        url: `/${lang}/admin/card-partner`,
        iconComponent: { name: 'cil-chart' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        name: t('blockchain_transactions'),
        url: `/${lang}/admin/blockchain-transactions`,
        iconComponent: { name: 'cil-link' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        name: t('audit_trail'),
        url: `/${lang}/admin/audit-trail`,
        iconComponent: { name: 'cil-history' },
        badge: admin('SUPER'),
        attributes: { 'data-visible': 'isSuperAdmin' },
      },
      {
        // Declared explicitly because this menu overrides createNavItems outright
        // rather than filtering super.createNavItems(), so it inherits nothing from
        // the shared credo menu. Admin-visible, matching the backend's ensure_admin!.
        name: t('legal_acceptances'),
        url: `/${lang}/admin/legal-acceptances`,
        iconComponent: { name: 'cil-check-circle' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        // Mirrors the shared credo menu entry (same reason as legal_acceptances).
        name: t('community.admin.menu'),
        url: `/${lang}/admin/community`,
        iconComponent: { name: 'cil-people' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
      {
        name: t('appointments.admin_menu'),
        url: `/${lang}/admin/appointments`,
        iconComponent: { name: 'cil-calendar' },
        badge: admin('ADMIN'),
        attributes: isAdmin,
      },
    ];
  }
}
